import { HttpErrorResponse } from "@angular/common/http";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { createReducer, on } from "@ngrx/store";
import { Circulation, CirculationId } from "src/app/interfaces";
import {
  CirculationActions,
  CirculationAPIActions,
  CirculationDocumentActions
} from "./actions";

export interface CirculationState extends EntityState<Circulation> {
  error: HttpErrorResponse | null;
  loaded: boolean;
  uploadDocumentFailed: boolean;
  uploadDocumentCompleted: boolean;
  savedCirculationId: CirculationId;
}

export const adapter: EntityAdapter<Circulation> =
  createEntityAdapter<Circulation>();

const {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  selectIds,
  selectEntities,
  selectAll,
  selectTotal
  /* eslint-enable @typescript-eslint/no-unused-vars */
} = adapter.getSelectors();

export const selectCirculationEntities = selectEntities;
export const selectAllCirculations = selectAll;
export const selectCirculationTotal = selectTotal;

const initialState: CirculationState = adapter.getInitialState({
  // additional entity state properties
  error: null,
  loaded: false,
  uploadDocumentFailed: false,
  uploadDocumentCompleted: true,
  savedCirculationId: null
});

export const circulationReducer = createReducer(
  initialState,
  on(CirculationActions.addCirculation, (state, action) =>
    adapter.addOne(action.circulation, state)
  ),
  on(
    CirculationActions.upsertCirculation,
    CirculationAPIActions.loadCirculationSuccess,
    (state, action) => adapter.upsertOne(action.circulation, state)
  ),
  on(CirculationActions.addCirculations, (state, action) =>
    adapter.addMany(action.circulations, state)
  ),
  on(
    CirculationActions.upsertCirculations,
    CirculationAPIActions.loadCirculationsSuccess,
    (state, action) => adapter.upsertMany(action.circulations, state)
  ),
  on(CirculationActions.updateCirculation, (state, action) =>
    adapter.updateOne(action.circulation, state)
  ),
  on(CirculationActions.updateCirculations, (state, action) =>
    adapter.updateMany(action.circulations, state)
  ),
  on(CirculationActions.deleteCirculation, (state, action) =>
    adapter.removeOne(action.id, state)
  ),
  on(CirculationActions.deleteCirculations, (state, action) =>
    adapter.removeMany(action.ids, state)
  ),
  on(CirculationActions.clearCirculations, (state) => adapter.removeAll(state)),
  on(
    CirculationAPIActions.loadCirculationsFailure,
    (state, action): CirculationState => {
      return {
        ...adapter.removeAll(state),
        error: action.error,
        loaded: false
      };
    }
  ),
  on(CirculationAPIActions.loadCirculation, (state): CirculationState => {
    return {
      ...state,
      loaded: false
    };
  }),
  on(
    CirculationAPIActions.loadCirculationSuccess,
    (state): CirculationState => {
      return {
        ...state,
        loaded: true
      };
    }
  ),
  on(
    CirculationAPIActions.loadCirculationsSuccess,
    (state): CirculationState => {
      return {
        ...state,
        loaded: true
      };
    }
  ),
  on(
    CirculationDocumentActions.uploadDocumentStarted,
    (state): CirculationState => {
      return {
        ...state,
        uploadDocumentFailed: false,
        uploadDocumentCompleted: false
      };
    }
  ),
  on(CirculationDocumentActions.removeDocument, (state): CirculationState => {
    return {
      ...state,
      uploadDocumentFailed: false,
      uploadDocumentCompleted: false
    };
  }),
  on(
    CirculationDocumentActions.removeDocumentSuccess,
    (state): CirculationState => {
      return {
        ...state,
        uploadDocumentCompleted: true
      };
    }
  ),
  on(
    CirculationDocumentActions.uploadDocumentCompleted,
    (state): CirculationState => {
      return {
        ...state,
        uploadDocumentCompleted: true
      };
    }
  ),
  on(
    CirculationDocumentActions.uploadDocumentFailed,
    (state): CirculationState => {
      return {
        ...state,
        uploadDocumentFailed: true
      };
    }
  ),
  on(
    CirculationAPIActions.saveCirculationFailure,
    (state, action): CirculationState => {
      return {
        ...state,
        savedCirculationId: null,
        error: action.error
      };
    }
  ),
  on(
    CirculationAPIActions.saveCirculationSuccess,
    (state, action): CirculationState => {
      return {
        ...state,
        savedCirculationId: action.id
      };
    }
  ),
  on(CirculationAPIActions.saveCirculation, (state): CirculationState => {
    return {
      ...state,
      savedCirculationId: null
    };
  })
);
